import * as Sentry from '@sentry/nuxt'
import { useRuntimeConfig, useRouter } from '#imports'

// Early exit for development environment - skip all Sentry setup
const environment = useRuntimeConfig().public.appEnv
const isDevelopment = environment === 'development'

if (isDevelopment) {
  // No-op: Sentry is completely disabled in development environment
  // Exit early to prevent any Sentry-related code execution
} else {
  const router = useRouter()

  // Skip Sentry initialization in test environment
  const isTestEnvironment =
    process.env.NODE_ENV === 'test' ||
    import.meta.env?.TEST ||
    (typeof process !== 'undefined' && process.env?.VITEST)

  const getErrorSampleRate = () => {
    switch (environment) {
      case 'production':
        return 0.2 // 20% of errors in production
      case 'homolog':
        return 1.0 // 100% of errors in homolog/staging
      default:
        return 0
    }
  }

  const getTracesSampleRate = () => {
    switch (environment) {
      case 'production':
        return 0.1 // 10% of traces in production
      case 'homolog':
        return 1.0 // 100% of traces in homolog/staging
      default:
        return 0
    }
  }

  // Build integrations array conditionally
  const integrations = []

  // Only add browserTracingIntegration if it exists and we're not in test
  if (
    !isTestEnvironment &&
    typeof Sentry.browserTracingIntegration === 'function'
  ) {
    integrations.push(Sentry.browserTracingIntegration({ router }))
  }

  // Only add replayIntegration if it exists and we're not in test
  if (!isTestEnvironment && typeof Sentry.replayIntegration === 'function') {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      })
    )
  }

  // Skip Sentry initialization in test environment
  if (isTestEnvironment) {
    // No-op: Sentry is not initialized in test environment
  } else {
    Sentry.init({
      // If set up, you can use your runtime config here
      dsn: useRuntimeConfig().public.sentry.dsn,
      environment, // Set environment for Sentry dashboard filtering
      ignoreErrors: [
        'Non-Error exception captured',
        'Non-Error promise rejection captured',
        // Network errors that are not actionable
        'NetworkError',
        'ERR_BLOCKED_BY_CLIENT',
        'ERR_NETWORK',
        // Ad blocker related errors
        'blocked by client',
        'blocked by extension',
        // Vue/Nuxt hydration warnings (not critical errors)
        'Hydration completed but contains mismatches',
        'Hydration node mismatch',
        'Hydration text mismatch',
        'hydration mismatch',
      ],

      integrations,

      tracePropagationTargets: [
        'localhost',
        'https://gokonfetti.com',
        'https://front-hml.gokonfetti.com',
      ],
      sampleRate: getErrorSampleRate(),
      tracesSampleRate: getTracesSampleRate(),
      // Enable debug mode for better error tracking
      debug: environment !== 'production',
      // Increase replay sample rate for errors
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.01, // 0.01 (1% of errors will have replay)

      // Handle transport errors gracefully
      beforeSend(event, hint) {
        // Log transport errors to console in development
        if (environment !== 'production' && hint.originalException) {
          const error = hint.originalException
          if (
            error instanceof Error &&
            error.message.includes('Failed to fetch')
          ) {
            // eslint-disable-next-line no-console
            console.warn(
              'Sentry transport blocked (likely by ad blocker or firewall):',
              error.message
            )
            return null // Don't send the event
          }
        }

        return event
      },

      // Use default transport (debug mode disabled for clean console)
      transport: undefined,
    })
  }
}
